import { useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import type { NoticeBoardPost } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, Modal, Spinner, StatusBadge } from '../../ui/components';

const DOCUMENTED_CATEGORIES = ['MINUTES', 'BYLAW'];

/**
 * DRAFT -> PUBLISHED -> WITHDRAWN. Publish issues a real DocVault-hashed
 * credential when a MINUTES/BYLAW post has a document reference attached
 * (docRef + sha256 must come from an already-uploaded DocVault document —
 * this portal has no file-upload pipeline into DocVault, so the fields
 * below are a genuine passthrough for a reference a staff member already
 * holds, not a fabricated uploader). BYLAW additionally publishes a public
 * OpenGov gazette notice. Withdraw revokes the DocVault credential if one
 * was issued. See m41-local-government/src/routes/notices.ts.
 */
export function NoticeDetail({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const noticeQ = useApiQuery<NoticeBoardPost>(`/api/v1/m41/notices/${id}`);
  const notice = noticeQ.data;

  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [pinned, setPinned] = useState(false);
  const [showOnPhysicalScreen, setShowOnPhysicalScreen] = useState(true);
  const [showPublish, setShowPublish] = useState(false);
  const [docRef, setDocRef] = useState('');
  const [sha256, setSha256] = useState('');
  const [fileName, setFileName] = useState('');
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState('');

  const startEdit = (n: NoticeBoardPost) => {
    setTitle(n.title);
    setBody(n.body);
    setPinned(n.pinned);
    setShowOnPhysicalScreen(n.showOnPhysicalScreen);
    setEditing(true);
  };

  const refresh = () => {
    noticeQ.reload();
    onChanged();
  };

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      refresh();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = () =>
    run(async () => {
      await api.patch(`/api/v1/m41/notices/${id}`, { title: title.trim(), body: body.trim(), pinned, showOnPhysicalScreen });
      setEditing(false);
    });

  const publish = () =>
    run(async () => {
      const attach = docRef.trim() && sha256.trim() ? { docRef: docRef.trim(), sha256: sha256.trim(), fileName: fileName.trim() || undefined } : undefined;
      await api.post(`/api/v1/m41/notices/${id}/publish`, attach);
      setShowPublish(false);
      setDocRef('');
      setSha256('');
      setFileName('');
    });

  const withdraw = () =>
    run(async () => {
      await api.post(`/api/v1/m41/notices/${id}/withdraw`, { reason: withdrawReason.trim() });
      setShowWithdraw(false);
      setWithdrawReason('');
    });

  const isDocumented = notice ? DOCUMENTED_CATEGORIES.includes(notice.category) : false;

  return (
    <Modal title={notice ? notice.title : 'Notice'} onClose={onClose} wide>
      {noticeQ.loading ? <Spinner /> : null}
      {noticeQ.error ? <ErrorNote message={noticeQ.error} /> : null}
      {actionError ? (
        <div className="mb-3">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      {notice && !editing ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={notice.status} />
            <span className="text-xs text-slate-500">{notice.category.replaceAll('_', ' ')}</span>
            {notice.pinned ? <span className="text-xs text-slate-500">· pinned</span> : null}
            {notice.showOnPhysicalScreen ? <span className="text-xs text-slate-500">· on physical screen</span> : null}
          </div>
          <p className="whitespace-pre-wrap text-sm text-slate-700">{notice.body}</p>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
            {notice.publishedAt ? (
              <div>
                <dt className="text-xs text-slate-500">Published</dt>
                <dd className="font-medium text-slate-800">{formatDateTime(notice.publishedAt)}</dd>
              </div>
            ) : null}
            {notice.expiresAt ? (
              <div>
                <dt className="text-xs text-slate-500">Expires</dt>
                <dd className="font-medium text-slate-800">{formatDateTime(notice.expiresAt)}</dd>
              </div>
            ) : null}
            {notice.docSerial ? (
              <div>
                <dt className="text-xs text-slate-500">DocVault serial</dt>
                <dd className="font-mono text-xs text-slate-800">{notice.docSerial}</dd>
              </div>
            ) : null}
            {notice.docVerifyUrl ? (
              <div>
                <dt className="text-xs text-slate-500">Verify URL</dt>
                <dd className="truncate text-xs text-brand-600">{notice.docVerifyUrl}</dd>
              </div>
            ) : null}
            {notice.openGovNoticeRef ? (
              <div>
                <dt className="text-xs text-slate-500">OpenGov gazette ref</dt>
                <dd className="font-mono text-xs text-slate-800">{notice.openGovNoticeRef}</dd>
              </div>
            ) : null}
          </dl>
          {notice.status === 'WITHDRAWN' && notice.withdrawnReason ? <ErrorNote message={`Withdrawn: ${notice.withdrawnReason}`} /> : null}

          <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
            {notice.status === 'DRAFT' ? (
              <>
                <button className="btn-secondary" disabled={busy} onClick={() => startEdit(notice)}>
                  Edit
                </button>
                <button className="btn-primary" disabled={busy} onClick={() => setShowPublish(true)}>
                  Publish
                </button>
              </>
            ) : null}
            {notice.status === 'PUBLISHED' ? (
              <button className="btn-danger" disabled={busy} onClick={() => setShowWithdraw(true)}>
                Withdraw
              </button>
            ) : null}
          </div>

          {showPublish ? (
            <div className="space-y-2 rounded-lg border border-brand-200 bg-brand-50 p-3">
              {isDocumented ? (
                <>
                  <p className="text-xs text-slate-600">
                    MINUTES/BYLAW posts can attach an already-issued DocVault document reference for a hashed,
                    independently verifiable credential — optional, and only meaningful if you already have one
                    (there is no file-upload pipeline here).
                    {notice.category === 'BYLAW' ? ' BYLAW posts also publish a public OpenGov gazette notice regardless.' : ''}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Document ref (optional)">
                      <input className="field-input" value={docRef} onChange={(e) => setDocRef(e.target.value)} placeholder="DocVault document id" />
                    </Field>
                    <Field label="SHA-256 (optional)">
                      <input className="field-input" value={sha256} onChange={(e) => setSha256(e.target.value)} placeholder="64 hex chars" />
                    </Field>
                  </div>
                  <Field label="File name (optional)">
                    <input className="field-input" value={fileName} onChange={(e) => setFileName(e.target.value)} />
                  </Field>
                </>
              ) : (
                <p className="text-xs text-slate-600">This category publishes immediately — no document to attach.</p>
              )}
              <div className="flex justify-end gap-2">
                <button className="btn-secondary" onClick={() => setShowPublish(false)}>
                  Back
                </button>
                <button
                  className="btn-primary"
                  disabled={busy || (Boolean(docRef.trim()) !== Boolean(sha256.trim()))}
                  onClick={publish}
                >
                  Confirm publish
                </button>
              </div>
            </div>
          ) : null}
          {showWithdraw ? (
            <div className="space-y-2 rounded-lg border border-rose-200 bg-rose-50 p-3">
              <Field label="Withdrawal reason">
                <textarea className="field-input" rows={2} value={withdrawReason} onChange={(e) => setWithdrawReason(e.target.value)} />
              </Field>
              <div className="flex justify-end gap-2">
                <button className="btn-secondary" onClick={() => setShowWithdraw(false)}>
                  Back
                </button>
                <button className="btn-danger" disabled={busy || withdrawReason.trim().length < 3} onClick={withdraw}>
                  Confirm withdraw
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {notice && editing ? (
        <div className="space-y-3">
          <Field label="Title">
            <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
          <Field label="Body">
            <textarea className="field-input" rows={5} value={body} onChange={(e) => setBody(e.target.value)} />
          </Field>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} />
              Pinned
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={showOnPhysicalScreen} onChange={(e) => setShowOnPhysicalScreen(e.target.checked)} />
              Show on physical office screen
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button className="btn-secondary" onClick={() => setEditing(false)}>
              Cancel
            </button>
            <button className="btn-primary" disabled={busy || title.trim().length < 2 || body.trim().length < 2} onClick={saveEdit}>
              {busy ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
